import { z } from 'zod'

import { proposedGet } from '@/api/proposedEndpoint'

/**
 * THE switch for the Broker rail's "Today's highlights". mm-be has no broker news feed
 * (`/market/updates/` carries company notes only), so this is `false`: no request is made, no
 * query runs, and the block draws its real head over the generic "Not available yet" state. Flip
 * it to `true` when the endpoint ships (`mm-brokers-2026-10-28/asks/broker-rail.md`).
 */
export const BROKER_HIGHLIGHTS_AVAILABLE: boolean = false

/** The proposed path: today's broker notes, newest first, public. */
const PATH = '/api/v5/brokers/highlights/'

/** The rail draws four. */
export const BROKER_HIGHLIGHTS_LIMIT = 4

/** Where a note opens: a broker's page, or one of the Broker Analysis lists it summarises. */
const highlightTargetSchema = z.enum(['broker', 'large-trades', 'most-traded', 'sector-flow'])

const highlightSchema = z.object({
  id: z.string().min(1),
  /** The eyebrow's first word: "Floorsheet", "Brokers". */
  source: z.string().min(1),
  /** AD `YYYY-MM-DD`; the eyebrow prints it in BS. */
  publishedOn: z.string().min(1),
  /** The house the note is about, if one: the eyebrow's "about Broker 58". */
  brokerCode: z.string().min(1).nullable(),
  title: z.string().min(1),
  target: highlightTargetSchema,
})

export type BrokerHighlight = z.infer<typeof highlightSchema>

/** One note as the proposed endpoint sends it. */
interface WireHighlight {
  id: string | number
  source: string
  published_on: string
  broker_code: string | null
  title: string
  target: string
}

export const brokerHighlightsService = {
  today: async (): Promise<BrokerHighlight[]> => {
    const rows = await proposedGet<WireHighlight[]>(PATH, { limit: BROKER_HIGHLIGHTS_LIMIT })
    return z.array(highlightSchema).parse(
      rows.map((row) => ({
        id: String(row.id),
        source: row.source,
        publishedOn: row.published_on,
        brokerCode: row.broker_code,
        title: row.title,
        target: row.target,
      })),
    )
  },
}
